import { useMemo } from 'react';
import type { ComponentType, ReactNode } from 'react';
import Select, { components as rsComponents } from 'react-select';
import { useTranslation } from 'react-i18next';

import SampleStatusPillShell from './SampleStatusPillShell';
import { statusSelectMenuStyles } from './statusSelectMenuStyles';
import { SAMPLE_APPROVAL_STATUS_OPTIONS, SAMPLE_APPROVAL_STATUS_DEFAULT } from '../../constants/sample-approval-status.constants';
import type { SampleApprovalStatus } from '../../constants/sample-approval-status.constants';

interface PillOption {
  value: SampleApprovalStatus;
  label: string;
  swatchClass: string;
}

/** Pill background per swatch class, from the shared App.css status tokens. */
const SWATCH_BG: Record<string, string> = {
  pending: 'var(--color-amber)',
  approved: 'var(--color-green)',
  revised: 'var(--color-amber)',
  rejected: 'var(--color-red)',
};

/**
 * A 10px status dot whose colour matches the option's pill background swatch
 * ({@link SWATCH_BG}), so each menu row's dot harmonises with the pill it selects
 * (e.g. the pink "Rejected" pill gets a pink dot, the amber "Pending" an amber dot).
 *
 * @param {object} props - Carries the swatch class driving the dot colour.
 * @returns {ReactNode} The coloured dot span.
 */
function StatusDot({ swatchClass }: { swatchClass: string }): ReactNode {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: SWATCH_BG[swatchClass] ?? SWATCH_BG.pending,
        marginRight: 8,
        flexShrink: 0,
      }}
    />
  );
}

const RSSingleValue = rsComponents.SingleValue as unknown as ComponentType<React.PropsWithChildren<{ data: PillOption }>>;
const RSOption = rsComponents.Option as unknown as ComponentType<React.PropsWithChildren<{ data: PillOption }>>;

/**
 * Renders the selected value as plain pill text — no status dot. The pill's own
 * pastel swatch background already conveys the status, so a dot there is redundant
 * (matching the internal sample-status pill's selected-value treatment). The dot
 * appears only in the open menu's option rows.
 *
 * @param {object} props - react-select single-value props carrying option data.
 * @returns {ReactNode} The pill label.
 */
function PillSingleValue(props: React.PropsWithChildren<{ data: PillOption }>): ReactNode {
  return <RSSingleValue {...props}>{props.data.label}</RSSingleValue>;
}

/**
 * Renders the option's translated label in the menu.
 *
 * @param {object} props - react-select option props carrying option data.
 * @returns {ReactNode} The option label.
 */
function PillOptionRenderer(props: React.PropsWithChildren<{ data: PillOption }>): ReactNode {
  return (
    <RSOption {...props}>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <StatusDot swatchClass={props.data.swatchClass} />
        <span>{props.data.label}</span>
      </span>
    </RSOption>
  );
}

export interface SampleApprovalStatusPillProps {
  /** Leading icon that doubles as the expand/collapse toggle. */
  icon: ReactNode;
  /** Accessible label for the toggle icon. */
  toggleLabel: string;
  /** Whether the status pill body is shown (column visible) or collapsed to the icon. */
  expanded: boolean;
  /** Toggles the column / pill body. */
  onToggle: () => void;
  /** Current stored status value. */
  value?: string | null;
  /** Fired with the newly selected status value. */
  onChange: (_next: SampleApprovalStatus) => void;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * Sample approval-status control (BM-9882) matching the Figma "Status Selection
 * Dropdown": a bordered unit whose leading icon toggles the column, followed by a
 * divider and a coloured hardcoded-status dropdown. When collapsed, only the icon
 * shows (the pill body is hidden) so the column can be reopened and the status
 * stays legible via its swatch colour.
 */
function SampleApprovalStatusPill({
  icon,
  toggleLabel,
  expanded,
  onToggle,
  value,
  onChange,
  disabled = false,
  'data-testid': dataTestId,
}: SampleApprovalStatusPillProps) {
  const { t } = useTranslation('product-sample');

  const options: PillOption[] = useMemo(
    () => SAMPLE_APPROVAL_STATUS_OPTIONS.map((option) => ({ value: option.value, swatchClass: option.swatchClass, label: t(option.labelKey) })),
    [t],
  );

  const selected = useMemo(() => options.find((option) => option.value === (value ?? SAMPLE_APPROVAL_STATUS_DEFAULT)) ?? options[0], [options, value]);

  const styles = useMemo(
    () => ({
      container: (base: Record<string, unknown>) => ({ ...base, flex: '1 0 0', minWidth: 0 }),
      control: (base: Record<string, unknown>) => ({
        ...base,
        minHeight: 24,
        height: 24,
        borderRadius: 3,
        border: 'none',
        backgroundColor: SWATCH_BG[selected?.swatchClass ?? 'pending'] ?? SWATCH_BG.pending,
        boxShadow: 'none',
      }),
      valueContainer: (base: Record<string, unknown>) => ({ ...base, padding: '0 8px' }),
      singleValue: (base: Record<string, unknown>) => ({ ...base, color: 'var(--color-text)', fontSize: 14 }),
      indicatorsContainer: () => ({ display: 'none' }),
      indicatorSeparator: () => ({ display: 'none' }),
      ...statusSelectMenuStyles,
    }),
    [selected],
  );

  return (
    <SampleStatusPillShell icon={icon} toggleLabel={toggleLabel} onToggle={onToggle} expanded={expanded} {...(dataTestId ? { 'data-testid': dataTestId } : {})}>
      <Select
        classNamePrefix="sample-approval-status"
        options={options}
        value={selected}
        onChange={(next: unknown) => {
          const option = next as PillOption | null;
          if (option) onChange(option.value);
        }}
        isSearchable={false}
        isDisabled={disabled}
        styles={styles}
        components={{ SingleValue: PillSingleValue, Option: PillOptionRenderer }}
        aria-label={dataTestId}
      />
    </SampleStatusPillShell>
  );
}

export default SampleApprovalStatusPill;
